import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Cell,
} from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useApiServices } from '../../services/api-endpoints/useApiServices';
import { useSignalR } from '../../services/signalR/SignalRProvider';
import { useLanguage } from '../../services/language/LanguageContext';
import { DateTimeFormat } from '../../helper/Formatters';
import dayjs from 'dayjs';

const fmt = (n) => Number(n).toLocaleString('th-TH');
const fmtShort = (n) => n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n);

const KpiCard = ({ icon, label, value, sub, color, trend }) => (
  <Col md={3} sm={6} className="mb-3">
    <div style={{
      background: '#fff', borderRadius: 12, padding: '16px 20px',
      boxShadow: '0 1px 8px rgba(0,0,0,0.07)', border: '1px solid #f3f4f6',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500, marginBottom: 4 }}>{label}</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>{value}</div>
          {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{sub}</div>}
        </div>
        <div style={{
          width: 40, height: 40, borderRadius: 10, background: `${color}18`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <i className={icon} style={{ fontSize: 18, color }} />
        </div>
      </div>
      {trend !== undefined && (
        <div style={{ marginTop: 10, fontSize: 11, color: trend >= 0 ? '#16a34a' : '#ef4444', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
          <i className={`fa fa-arrow-${trend >= 0 ? 'up' : 'down'}`} style={{ fontSize: 9 }} />
          {Math.abs(trend)}% เทียบกับช่วงก่อนหน้า
        </div>
      )}
    </div>
  </Col>
);

const StatCard = ({ icon, label, value, color, to }) => (
  <Col md={3} sm={6} className="mb-4">
    <Card style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
      <Card.Body style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 24px' }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className={icon} style={{ fontSize: 22, color }} />
        </div>
        <div>
          <div style={{ fontSize: 13, color: '#9ca3af', fontWeight: 500 }}>{label}</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#1f2937', lineHeight: 1.2, marginTop: 2 }}>
            {value !== null ? value.toLocaleString() : <span style={{ fontSize: 16, color: '#9ca3af' }}>...</span>}
          </div>
        </div>
        {to && (
          <Link to={to} style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 18 }}>
            <i className="fa fa-arrow-right" />
          </Link>
        )}
      </Card.Body>
    </Card>
  </Col>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      <div style={{ fontWeight: 700, color: '#374151', marginBottom: 6 }}>{payload[0]?.payload?.fullLabel || label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color, display: 'flex', alignItems: 'center', gap: 6 }}>
          <i className="fa fa-circle" style={{ fontSize: 7 }} />
          {p.name}: <b>{p.dataKey === 'revenue' ? `฿${fmt(p.value)}` : fmt(p.value)}</b>
        </div>
      ))}
    </div>
  );
};

const LoadingOverlay = () => (
  <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
    <i className="fa fa-spinner fa-spin" style={{ fontSize: 20 }} />
  </div>
);

// Modern calendar CSS override
const DATEPICKER_STYLES = `
  .db-datepicker .react-datepicker {
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.13);
    font-family: inherit;
    overflow: hidden;
    padding: 0;
  }
  .db-datepicker .react-datepicker__triangle { display: none; }
  .db-datepicker .react-datepicker__header {
    background: #fff;
    border-bottom: 1px solid #f3f4f6;
    padding: 16px 16px 10px;
    border-radius: 0;
  }
  .db-datepicker .react-datepicker__current-month {
    font-size: 14px; font-weight: 700; color: #1f2937; margin-bottom: 10px;
  }
  .db-datepicker .react-datepicker__day-names { margin: 0; }
  .db-datepicker .react-datepicker__day-name {
    color: #9ca3af; font-size: 11px; font-weight: 700;
    width: 2.1rem; line-height: 2.1rem; text-transform: uppercase;
  }
  .db-datepicker .react-datepicker__month { padding: 8px 10px 12px; margin: 0; }
  .db-datepicker .react-datepicker__day {
    border-radius: 8px; font-size: 12px; font-weight: 500; color: #374151;
    width: 2.1rem; line-height: 2.1rem; transition: all 0.12s;
  }
  .db-datepicker .react-datepicker__day:hover {
    background: #e0e7ff; color: #4f46e5; border-radius: 8px;
  }
  .db-datepicker .react-datepicker__day--selected,
  .db-datepicker .react-datepicker__day--keyboard-selected {
    background: #4f46e5; color: #fff; border-radius: 8px;
    font-weight: 700;
  }
  .db-datepicker .react-datepicker__day--selected:hover {
    background: #4338ca; color: #fff;
  }
  .db-datepicker .react-datepicker__day--today {
    font-weight: 700; color: #4f46e5;
  }
  .db-datepicker .react-datepicker__day--today.react-datepicker__day--selected { color: #fff; }
  .db-datepicker .react-datepicker__day--outside-month { color: #d1d5db; }
  .db-datepicker .react-datepicker__navigation {
    top: 14px;
  }
  .db-datepicker .react-datepicker__navigation-icon::before {
    border-color: #6b7280; border-width: 2px 2px 0 0; width: 8px; height: 8px;
  }
  .db-datepicker .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
    border-color: #4f46e5;
  }
  .db-datepicker .react-datepicker-popper {
    z-index: 9999;
  }
`;

const CustomDateInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <button
    ref={ref}
    onClick={onClick}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      padding: '7px 14px', borderRadius: 9,
      background: '#f8faff', border: '1.5px solid #c7d2fe',
      color: '#374151', fontSize: 13, fontWeight: 600,
      cursor: 'pointer', whiteSpace: 'nowrap', outline: 'none',
    }}
  >
    <i className="fa fa-calendar-o" style={{ color: '#4f46e5', fontSize: 11 }} />
    {value || placeholder}
    <i className="fa fa-angle-down" style={{ color: '#a5b4fc', fontSize: 11, marginLeft: 1 }} />
  </button>
));

const BAR_COLORS = ['#4f46e5', '#7c3aed', '#2563eb', '#0891b2', '#0d9488'];

const Dashboard = () => {
  const api = useApiServices();
  const { messages } = useSignalR();
  const { t } = useLanguage();

  // Global counts
  const [stats, setStats] = useState({ transactions: null, products: null, stores: null, users: null });

  // Recent transactions
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);

  // Dashboard analytics data
  const [dailyData, setDailyData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [topStores, setTopStores] = useState([]);
  const [loadingChart, setLoadingChart] = useState(true);

  // Date range filter
  const defaultEnd = dayjs().format('YYYY-MM-DD');
  const defaultStart = dayjs().subtract(29, 'day').format('YYYY-MM-DD');
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [appliedStart, setAppliedStart] = useState(defaultStart);
  const [appliedEnd, setAppliedEnd] = useState(defaultEnd);

  // Chart mode
  const [chartType, setChartType] = useState('revenue');

  // KPI derived from daily data
  const kpi = useMemo(() => {
    const totalRevenue = dailyData.reduce((s, d) => s + (d.revenue || 0), 0);
    const totalOrders = dailyData.reduce((s, d) => s + (d.orders || 0), 0);
    const totalNewUsers = dailyData.reduce((s, d) => s + (d.newUsers || 0), 0);
    const avgOrder = totalOrders > 0 ? Math.floor(totalRevenue / totalOrders) : 0;
    return { totalRevenue, totalOrders, totalNewUsers, avgOrder };
  }, [dailyData]);

  // Chart data with thinned X labels
  const chartData = useMemo(() => {
    if (dailyData.length <= 14) return dailyData;
    const step = Math.ceil(dailyData.length / 14);
    return dailyData.map((d, i) => ({ ...d, label: i % step === 0 ? d.label : '' }));
  }, [dailyData]);

  const diffDays = dayjs(appliedEnd).diff(dayjs(appliedStart), 'day') + 1;

  const fetchStats = async () => {
    const [txRes, prodRes, storeRes, userRes] = await Promise.allSettled([
      api.GetTransactionAsync({ pageNumber: 1, pageSize: 1 }),
      api.GetProductsAsync({ pageNumber: 1, pageSize: 1 }),
      api.GetStoresAsync({ pageNumber: 1, pageSize: 1 }),
      api.GetUserListAsync({ pageNumber: 1, pageSize: 1, filterOptions: { isAdmin: true } }),
    ]);
    setStats({
      transactions: txRes.status === 'fulfilled' && txRes.value?.isSuccess ? txRes.value.result?.totalCount ?? txRes.value.result?.items?.length ?? null : null,
      products: prodRes.status === 'fulfilled' && prodRes.value?.isSuccess ? prodRes.value.result?.totalCount ?? prodRes.value.result?.items?.length ?? null : null,
      stores: storeRes.status === 'fulfilled' && storeRes.value?.isSuccess ? storeRes.value.result?.totalCount ?? storeRes.value.result?.items?.length ?? null : null,
      users: userRes.status === 'fulfilled' && userRes.value?.isSuccess ? userRes.value.result?.totalCount ?? userRes.value.result?.items?.length ?? null : null,
    });
  };

  const fetchRecentTransactions = async () => {
    setLoadingRecent(true);
    const { isSuccess, result } = await api.GetTransactionAsync({ pageNumber: 1, pageSize: 8 });
    if (isSuccess) setRecentTransactions(result.items || []);
    setLoadingRecent(false);
  };

  const fetchDashboardData = async (start, end) => {
    setLoadingChart(true);
    const params = { startDate: start, endDate: end };
    const [dailyRes, productsRes, storesRes] = await Promise.allSettled([
      api.GetDashboardDailyAsync(params),
      api.GetDashboardTopProductsAsync({ ...params, limit: 5 }),
      api.GetDashboardTopStoresAsync({ ...params, limit: 5 }),
    ]);

    if (dailyRes.status === 'fulfilled' && dailyRes.value?.isSuccess) {
      const mapped = (dailyRes.value.result || []).map((d) => ({
        ...d,
        label: dayjs(d.date).format('D/M'),
        fullLabel: dayjs(d.date).format('D MMM YYYY'),
      }));
      setDailyData(mapped);
    } else {
      setDailyData([]);
    }

    if (productsRes.status === 'fulfilled' && productsRes.value?.isSuccess) {
      setTopProducts(productsRes.value.result || []);
    } else {
      setTopProducts([]);
    }

    if (storesRes.status === 'fulfilled' && storesRes.value?.isSuccess) {
      setTopStores(storesRes.value.result || []);
    } else {
      setTopStores([]);
    }

    setLoadingChart(false);
  };

  useEffect(() => {
    fetchStats();
    fetchRecentTransactions();
    fetchDashboardData(defaultStart, defaultEnd);
  }, []);

  useEffect(() => {
    if (messages?.length > 0) {
      const last = messages[messages.length - 1];
      if (last && (last.type === 'Transaction' || last.type === 'Payment')) {
        fetchStats();
        fetchRecentTransactions();
      }
    }
  }, [messages]);

  const handleApplyFilter = () => {
    if (startDate && endDate && startDate <= endDate) {
      setAppliedStart(startDate);
      setAppliedEnd(endDate);
      fetchDashboardData(startDate, endDate);
    }
  };

  const handlePreset = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setAppliedStart(start);
    setAppliedEnd(end);
    fetchDashboardData(start, end);
  };

  return (
    <React.Fragment>
      <style>{DATEPICKER_STYLES}</style>

      {/* ── Global stat cards ───────────────────────────────── */}
      <Row className="mb-2">
        <StatCard icon="fa fa-truck" label={t('stat_transactions')} value={stats.transactions} color="#3b82f6" to="/transactions" />
        <StatCard icon="fa fa-cubes" label={t('stat_products')} value={stats.products} color="#10b981" to="/products" />
        <StatCard icon="fa fa-shopping-bag" label={t('stat_stores')} value={stats.stores} color="#f59e0b" to="/stores" />
        <StatCard icon="fa fa-users" label={t('stat_users')} value={stats.users} color="#8b5cf6" to="/users" />
      </Row>

      {/* ── Date range filter ─────────────────────────────────── */}
      <div style={{ background: '#fff', borderRadius: 14, padding: '16px 20px', marginBottom: 20, boxShadow: '0 1px 8px rgba(0,0,0,0.07)', border: '1px solid #f3f4f6' }}>

        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="fas fa-sliders-h" style={{ color: '#4f46e5', fontSize: 12 }} />{t('date_range')}
          </span>
          {loadingChart ? (
            <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5 }}>
              <i className="fa fa-spinner fa-spin" style={{ color: '#4f46e5' }} />กำลังโหลด...
            </span>
          ) : (
            <span style={{ fontSize: 11, color: '#9ca3af', background: '#f3f4f6', borderRadius: 6, padding: '2px 10px' }}>
              {dayjs(appliedStart).format('D MMM')} – {dayjs(appliedEnd).format('D MMM YYYY')}
              <span style={{ color: '#d1d5db', margin: '0 5px' }}>·</span>{diffDays} {t('days_unit')}
            </span>
          )}
        </div>

        {/* Controls row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>

          {/* Preset segmented buttons */}
          {(() => {
            const today = dayjs().format('YYYY-MM-DD');
            const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
            const presets = [
              { label: t('today'),     start: today,                                end: today },
              { label: t('yesterday'), start: yesterday,                            end: yesterday },
              { label: `7 ${t('days_unit')}`,  start: dayjs().subtract(6, 'day').format('YYYY-MM-DD'),  end: today },
              { label: `30 ${t('days_unit')}`, start: dayjs().subtract(29, 'day').format('YYYY-MM-DD'), end: today },
              { label: `90 ${t('days_unit')}`, start: dayjs().subtract(89, 'day').format('YYYY-MM-DD'), end: today },
            ];
            return (
              <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 10, padding: 3, gap: 2 }}>
                {presets.map((p) => {
                  const active = appliedStart === p.start && appliedEnd === p.end;
                  return (
                    <button key={p.label} onClick={() => handlePreset(p.start, p.end)} style={{
                      fontSize: 12, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
                      background: active ? '#4f46e5' : 'transparent',
                      color: active ? '#fff' : '#6b7280',
                      border: 'none', transition: 'all 0.15s', whiteSpace: 'nowrap',
                      boxShadow: active ? '0 2px 8px rgba(79,70,229,0.3)' : 'none',
                    }}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
            );
          })()}

          {/* Divider */}
          <div style={{ width: 1, height: 30, background: '#e5e7eb' }} />

          {/* Custom date range */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DatePicker
              selected={dayjs(startDate).toDate()}
              onChange={(date) => setStartDate(dayjs(date).format('YYYY-MM-DD'))}
              maxDate={dayjs(endDate).toDate()}
              dateFormat="d MMM yyyy"
              customInput={<CustomDateInput />}
              popperClassName="db-datepicker"
              popperPlacement="bottom-start"
            />
            <div style={{ color: '#c7d2fe', fontSize: 18 }}>
              <i className="fa fa-long-arrow-right" />
            </div>
            <DatePicker
              selected={dayjs(endDate).toDate()}
              onChange={(date) => setEndDate(dayjs(date).format('YYYY-MM-DD'))}
              minDate={dayjs(startDate).toDate()}
              maxDate={new Date()}
              dateFormat="d MMM yyyy"
              customInput={<CustomDateInput />}
              popperClassName="db-datepicker"
              popperPlacement="bottom-start"
            />
          </div>

          {/* Apply */}
          <button onClick={handleApplyFilter} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 18px', borderRadius: 9,
            background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
            border: 'none', color: '#fff', fontSize: 12, fontWeight: 700,
            cursor: 'pointer', letterSpacing: '0.01em',
            boxShadow: '0 2px 10px rgba(79,70,229,0.35)',
          }}>
            <i className="fa fa-search" style={{ fontSize: 10 }} />{t('apply_filter')}
          </button>
        </div>
      </div>

      {/* ── Period KPI cards ─────────────────────────────────── */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
          {t('period_summary')} · {dayjs(appliedStart).format('D MMM')} – {dayjs(appliedEnd).format('D MMM YYYY')} ({diffDays} {t('days_unit')})
        </div>
        <Row>
          <KpiCard icon="fa fa-cash" label={t('total_revenue')} value={`฿${fmtShort(kpi.totalRevenue)}`} sub={`฿${fmt(kpi.totalRevenue)}`} color="#10b981" />
          <KpiCard icon="fa fa-shopping-cart" label={t('orders_in_period')} value={fmt(kpi.totalOrders)} sub={t('orders_unit')} color="#3b82f6" />
          <KpiCard icon="fa fa-calculator" label={t('avg_order_value')} value={`฿${fmt(kpi.avgOrder)}`} sub="ต่อออเดอร์" color="#f59e0b" />
          <KpiCard icon="fa fa-user-plus" label={t('new_users_period')} value={fmt(kpi.totalNewUsers)} sub={`${t('new_users_period')} ใหม่`} color="#8b5cf6" />
        </Row>
      </div>

      {/* ── Revenue chart ─────────────────────────────────────── */}
      <Card style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', marginBottom: 20 }}>
        <Card.Header style={{ background: '#fff', borderBottom: '1px solid #f3f4f6', borderRadius: '12px 12px 0 0', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
            <i className="fas fa-chart-area" style={{ marginRight: 8, color: '#4f46e5' }} />{t('revenue_trend')}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            {[{ key: 'revenue', label: t('col_revenue'), icon: 'fa fa-line-chart' }, { key: 'orders', label: t('col_orders'), icon: 'fa fa-bar-chart' }].map(({ key, label, icon }) => (
              <button key={key} onClick={() => setChartType(key)} style={{
                fontSize: 11, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontWeight: 600,
                background: chartType === key ? '#4f46e5' : '#f3f4f6',
                color: chartType === key ? '#fff' : '#6b7280',
                border: `1px solid ${chartType === key ? '#4f46e5' : '#e5e7eb'}`,
              }}>
                <i className={icon} style={{ marginRight: 4 }} />{label}
              </button>
            ))}
          </div>
        </Card.Header>
        <Card.Body style={{ padding: '20px 8px 10px 8px' }}>
          {loadingChart ? <LoadingOverlay /> : chartData.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              <i className="fa fa-bar-chart" style={{ fontSize: 24, display: 'block', marginBottom: 8, opacity: 0.4 }} />
              {t('no_data')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData} margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval={0} />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} width={44} />
                <Tooltip content={<CustomTooltip />} />
                {chartType === 'revenue' ? (
                  <Area type="monotone" dataKey="revenue" name={t('col_revenue')} stroke="#4f46e5" strokeWidth={2} fill="url(#colorRevenue)" dot={false} activeDot={{ r: 4 }} />
                ) : (
                  <Area type="monotone" dataKey="orders" name={t('col_orders')} stroke="#3b82f6" strokeWidth={2} fill="url(#colorOrders)" dot={false} activeDot={{ r: 4 }} />
                )}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card.Body>
      </Card>

      {/* ── Top Products + Top Stores ─────────────────────────── */}
      <Row className="mb-4">
        <Col md={6} className="mb-3">
          <Card style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', height: '100%' }}>
            <Card.Header style={cardHeaderStyle}>
              <span style={cardTitleStyle}>
                <i className="fa fa-star" style={{ marginRight: 8, color: '#f59e0b' }} />{t('top_products')}
              </span>
            </Card.Header>
            <Card.Body style={{ padding: '8px 0' }}>
              {loadingChart ? <LoadingOverlay /> : topProducts.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>{t('no_data')}</div>
              ) : topProducts.map((p, i) => (
                <div key={i} style={rankRowStyle}>
                  <div style={{ width: 22, height: 22, borderRadius: 6, background: i === 0 ? '#fef3c7' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: i === 0 ? '#d97706' : '#9ca3af', flexShrink: 0 }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{p.sales} {t('col_sales')}</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#10b981', flexShrink: 0 }}>฿{fmtShort(p.revenue)}</div>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} className="mb-3">
          <Card style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', height: '100%' }}>
            <Card.Header style={cardHeaderStyle}>
              <span style={cardTitleStyle}>
                <i className="fa fa-shopping-bag" style={{ marginRight: 8, color: '#3b82f6' }} />{t('top_stores')}
              </span>
            </Card.Header>
            <Card.Body style={{ padding: '4px 20px 10px 20px' }}>
              {loadingChart ? <LoadingOverlay /> : topStores.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>{t('no_data')}</div>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={topStores} layout="vertical" margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <XAxis type="number" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#374151' }} axisLine={false} tickLine={false} width={130} />
                    <Tooltip formatter={(v) => [`฿${fmt(v)}`, t('col_revenue')]} />
                    <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                      {topStores.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Recent Transactions ───────────────────────────────── */}
      <Row>
        <Col md={12}>
          <Card style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <Card.Header style={{ background: '#fff', borderBottom: '1px solid #f3f4f6', borderRadius: '12px 12px 0 0', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h5 style={{ margin: 0, fontWeight: 700 }}>{t('recent_transactions')}</h5>
                <small style={{ color: '#9ca3af' }}>{t('realtime_update')}</small>
              </div>
              <Link to="/transactions">
                <Button variant="outline-primary" size="sm">{t('view_all')}</Button>
              </Link>
            </Card.Header>
            <Card.Body style={{ padding: 0 }}>
              {loadingRecent ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
                  <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />{t('loading')}
                </div>
              ) : recentTransactions.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
                  <i className="fa fa-inbox" style={{ fontSize: 32, display: 'block', marginBottom: 8 }} />
                  {t('no_data')}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                        {[t('col_number'), t('col_store'), t('col_customer'), t('col_products'), t('col_status'), t('col_date')].map((h) => (
                          <th key={h} style={{ padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {recentTransactions.map((tx) => (
                        <tr key={tx.id} style={{ borderBottom: '1px solid #f9fafb', transition: 'background 0.15s' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '12px 16px' }}>
                            <Link to={`/transactions/${tx.id}`} style={{ fontWeight: 600, color: '#1e3a5f' }}>{tx.transactionNumber}</Link>
                          </td>
                          <td style={{ padding: '12px 16px', color: '#374151' }}>
                            <Link to={`/stores/${tx.store?.id}`} style={{ color: '#374151' }}>{tx.store?.name || '-'}</Link>
                          </td>
                          <td style={{ padding: '12px 16px', color: '#374151' }}>
                            <Link to={`/users/${tx.userId}`} style={{ color: '#374151' }}>
                              {tx.user?.firstName || ''} {tx.user?.lastName || ''}
                            </Link>
                          </td>
                          <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: 13 }}>
                            {tx.transactionToProducts?.slice(0, 2).map((p) => p.product?.nameTh).join(', ') || '-'}
                            {tx.transactionToProducts?.length > 2 && ` +${tx.transactionToProducts.length - 2}`}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, background: tx.status?.color || '#6c757d', color: '#fff', fontSize: 12, fontWeight: 500 }}>
                              {tx.status?.labelTh || '-'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', color: '#9ca3af', fontSize: 13 }}>
                            {DateTimeFormat(tx.createDate)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

    </React.Fragment>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────
const cardHeaderStyle = {
  background: '#fff', borderBottom: '1px solid #f3f4f6',
  borderRadius: '12px 12px 0 0', padding: '14px 20px',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};
const cardTitleStyle = { fontWeight: 700, fontSize: 14, color: '#111827' };
const rankRowStyle = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '9px 20px', borderBottom: '1px solid #f9fafb',
};

export default Dashboard;
